import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "../auth/supabase";
import {
  addWishlistItem,
  deleteWishlistItem,
  getWishlistItems,
} from "../services/wishList.js";

const WishlistContext = createContext();

export const WishlistProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [mutatedStates, setMutatedStates] = useState(new Map());
  const [serverStates, setServerStates] = useState(new Set());
  const [wishlistCount, setWishlistCount] = useState(0);
  const [showWishlistAuth, setShowWishlistAuth] = useState(false);

  const fetchWishlist = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) {
      setWishlistCount(0);
      setServerStates(new Set());
      return;
    }

    try {
      // perPage=0 asks the API for all IDs once. This is session/auth-event
      // hydration, not per-product polling.
      const res = await getWishlistItems(1, 0);

      if (res.success) {
        const items = Array.isArray(res.data?.wishlist_card_list)
          ? res.data.wishlist_card_list
          : Array.isArray(res.data?.items)
          ? res.data.items
          : Object.values(res.data?.wishlist_meta || {});
        setWishlistCount(items.length);
        setServerStates(new Set(items.map((item) => String(item.item_id || item.id || "")).filter(Boolean)));
        return;
      }

      setWishlistCount(0);
      setServerStates(new Set());
    } catch (error) {
      console.error("Wishlist count fetch failed:", error);
      setWishlistCount(0);
      setServerStates(new Set());
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setIsLoaded(true);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setIsLoaded(true);
    });
    return () => data.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!isLoaded) return;
    if (session) {
      fetchWishlist();
    } else {
      setMutatedStates(new Map());
      setServerStates(new Set());
      setWishlistCount(0);
    }
  }, [fetchWishlist, isLoaded, session]);

  const getWishlistStatus = useCallback((productId, backendStatus) => {
    const cleanId = typeof productId === "object" && productId?.$oid ? productId.$oid : String(productId || "");
    if (mutatedStates.has(cleanId)) {
      return mutatedStates.get(cleanId);
    }
    return serverStates.has(cleanId) || !!backendStatus;
  }, [mutatedStates, serverStates]);

  const toggleWishlist = async (productId, currentStatus) => {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) {
      setShowWishlistAuth(true);
      return;
    }

    const cleanProductId = typeof productId === "object" && productId?.$oid ? productId.$oid : String(productId || "");
    const newStatus = !currentStatus;

    // 1. Optimistic Update
    setMutatedStates((prev) => {
      const updated = new Map(prev);
      updated.set(cleanProductId, newStatus);
      return updated;
    });
    setWishlistCount((prev) => (newStatus ? prev + 1 : Math.max(0, prev - 1)));

    try {
      let res;
      if (currentStatus) {
        res = await deleteWishlistItem(cleanProductId, "item");
      } else {
        res = await addWishlistItem(cleanProductId);
      }

      if (!res.success) {
        throw new Error(res.error || "Failed to update wishlist");
      }
      setServerStates((prev) => {
        const updated = new Set(prev);
        if (newStatus) updated.add(cleanProductId);
        else updated.delete(cleanProductId);
        return updated;
      });
    } catch (error) {
      // Revert state on failure
      setMutatedStates((prev) => {
        const updated = new Map(prev);
        updated.set(cleanProductId, currentStatus);
        return updated;
      });
      setWishlistCount((prev) => (currentStatus ? prev + 1 : Math.max(0, prev - 1)));
      alert(error.message || "Failed to update wishlist. Please try again.");
    }
  };

  return (
    <WishlistContext.Provider
      value={{
        wishlistCount,
        getWishlistStatus,
        toggleWishlist,
        showWishlistAuth,
        setShowWishlistAuth,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => useContext(WishlistContext);
